'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Bell, Mail, Smartphone, Calendar, CheckCircle2, Loader2 } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';


interface AlertForm {
  email: string;
  mobile: string;
  alertDate: string;
  notifyEmail: boolean;
  notifySMS: boolean;
  notifyPush: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  blNumber: string;
  carrier: string;
}

export default function AlertSubscribeModal({ open, onClose, blNumber, carrier }: Props) {
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<AlertForm>({
    defaultValues: { notifyEmail: true, notifySMS: false, notifyPush: false },
  });

  const onSubmit = async (data: AlertForm) => {
    // Backend integration point: POST to /api/alerts with BL number, email, mobile, alertDate, channels
    await new Promise((r) => setTimeout(r, 1200));
    setSubmitted(true);
    toast.success(`Alert set for BL ${blNumber} on ${data.alertDate}`);
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setSubmitted(false);
      reset();
    }, 300);
  };

  return (
    <Modal open={open} onClose={handleClose} title={`Set Alert — ${blNumber}`}>
      {submitted ? (
        <div className="flex flex-col items-center gap-4 py-4">
          <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center">
            <CheckCircle2 size={28} className="text-success" />
          </div>
          <div className="text-center">
            <p className="text-base font-semibold text-foreground">Alert Scheduled!</p>
            <p className="text-sm text-muted-foreground mt-1">
              You'll receive notifications for BL <span className="font-semibold">{blNumber}</span> on the selected date and whenever the status changes.
            </p>
          </div>
          <button onClick={handleClose} className="btn-primary px-6 py-2.5 text-sm mt-2">
            Done
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
          {/* BL info */}
          <div className="bg-secondary rounded-lg px-4 py-3 flex items-center gap-3">
            <Bell size={16} className="text-primary shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Tracking</p>
              <p className="text-sm font-semibold text-foreground font-tabular">{blNumber} · {carrier}</p>
            </div>
          </div>

          {/* Alert date */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">
              Alert Date <span className="text-danger">*</span>
            </label>
            <p className="text-xs text-muted-foreground mb-2">
              We'll send you a reminder on this date about your shipment status.
            </p>
            <div className="relative">
              <Calendar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <input
                type="date"
                {...register('alertDate', { required: 'Please select an alert date.' })}
                className="input-field w-full h-11 pl-9 pr-4 text-sm"
              />
            </div>
            {errors.alertDate && (
              <p className="text-xs text-danger mt-1">{errors.alertDate.message}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">
              Email Address <span className="text-danger">*</span>
            </label>
            <div className="relative">
              <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <input
                type="email"
                placeholder="yourname@company.com"
                {...register('email', {
                  required: 'Email is required.',
                  pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address.' },
                })}
                className="input-field w-full h-11 pl-9 pr-4 text-sm"
              />
            </div>
            {errors.email && (
              <p className="text-xs text-danger mt-1">{errors.email.message}</p>
            )}
          </div>

          {/* Mobile */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">
              Mobile Number (India)
            </label>
            <p className="text-xs text-muted-foreground mb-2">Optional — for SMS alerts</p>
            <div className="flex gap-2">
              <div className="h-11 px-3 flex items-center text-sm font-semibold text-foreground bg-muted border border-border rounded-lg shrink-0">
                +91
              </div>
              <div className="relative flex-1">
                <Smartphone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input
                  type="tel"
                  placeholder="98765 43210"
                  {...register('mobile', {
                    pattern: { value: /^[6-9]\d{9}$/, message: 'Enter a valid 10-digit Indian mobile number.' },
                  })}
                  className="input-field w-full h-11 pl-9 pr-4 text-sm"
                />
              </div>
            </div>
            {errors.mobile && (
              <p className="text-xs text-danger mt-1">{errors.mobile.message}</p>
            )}
          </div>

          {/* Notification channels */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              Notification Channels
            </label>
            <div className="flex flex-col gap-2">
              {[
                { name: 'notifyEmail' as const, label: 'Email Notifications', icon: Mail },
                { name: 'notifySMS' as const, label: 'SMS Notifications', icon: Smartphone },
                { name: 'notifyPush' as const, label: 'Push Notifications (Browser)', icon: Bell },
              ].map((ch) => {
                const Icon = ch.icon;
                return (
                  <label
                    key={`ch-${ch.name}`}
                    className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      {...register(ch.name)}
                      className="w-4 h-4 accent-primary rounded"
                    />
                    <Icon size={15} className="text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground">{ch.label}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary h-11 flex items-center justify-center gap-2 text-sm mt-1"
          >
            {isSubmitting ? (
              <><Loader2 size={16} className="animate-spin" /> Setting Alert...</>
            ) : (
              <><Bell size={16} /> Confirm Alert</>
            )}
          </button>
        </form>
      )}
    </Modal>
  );
}